<template>
  <div class="flex min-h-screen flex-col bg-night">
    <AppHeader />

    <main class="mx-auto flex w-full max-w-6xl flex-col gap-7 px-4 pb-10 pt-6 sm:gap-8 sm:px-6 sm:pt-10">
      <!-- Hero -->
      <section class="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div class="flex items-center gap-4 sm:gap-6">
          <img
            src="../assets/logo.webp"
            alt="Drunken Hippos Logo"
            class="animate-float h-24 w-24 flex-none rounded-full object-cover ring-4 ring-hippo/60 sm:h-36 sm:w-36"
          />
          <div class="flex flex-col gap-1.5">
            <span class="text-[13px] font-semibold uppercase tracking-[0.08em] text-beer">Stammtisch-Statistik</span>
            <h1 class="font-display text-[40px] font-extrabold leading-none tracking-tight sm:text-[64px]">Saison {{ year }}</h1>
          </div>
        </div>
        <div role="group" aria-label="Saison wählen" class="flex gap-2">
          <button
            v-for="option in yearOptions"
            :key="option"
            @click="year = option"
            :aria-pressed="option === year"
            class="min-h-[44px] rounded-full border px-5 text-[15px] font-semibold transition-colors"
            :class="option === year ? 'border-beer bg-beer text-night' : 'border-edge bg-panel text-slate-300 hover:border-slate-500'"
          >
            {{ option }}
          </button>
        </div>
      </section>

      <div v-if="loading && !season" class="flex h-64 items-center justify-center">
        <div class="h-10 w-10 animate-spin rounded-full border-2 border-edge border-t-beer" aria-label="Lädt"></div>
      </div>

      <div v-else-if="error" class="flex flex-col items-center gap-4 py-16 text-center">
        <p class="text-red-300">{{ error }}</p>
        <button @click="load" class="min-h-[44px] rounded-full bg-beer px-5 font-semibold text-night">Erneut versuchen</button>
      </div>

      <template v-else-if="season">
        <!-- Kennzahlen -->
        <section aria-label="Kennzahlen" class="grid grid-cols-2 gap-2.5 transition-opacity sm:gap-4 lg:grid-cols-4" :class="{ 'opacity-60': loading }">
          <div
            v-for="kpi in kpis"
            :key="kpi.label"
            class="flex flex-col gap-1 rounded-[18px] border border-edge bg-panel p-3.5 transition-transform duration-300 hover:-translate-y-0.5 sm:rounded-[20px] sm:p-5"
          >
            <span class="text-xs font-medium text-slate-400 sm:text-[13px]">{{ kpi.label }}</span>
            <CountUp
              :value="kpi.value"
              :format="kpi.format"
              class="font-display text-[28px] font-extrabold tracking-tight sm:text-[40px]"
              :class="kpi.accent"
            />
            <span class="text-xs text-slate-400 sm:text-[13px]">{{ kpi.hint }}</span>
          </div>
        </section>

        <div class="grid grid-cols-[minmax(0,1fr)] gap-7 sm:gap-4 lg:grid-cols-2">
          <HallOfShame v-reveal :fines="season.fines" />
          <CategoryBreakdown v-reveal :categories="season.categories" :fines="season.fines" :details="season.fineDetails" />
        </div>

        <div class="grid grid-cols-[minmax(0,1fr)] gap-7 sm:gap-4 lg:grid-cols-2">
          <FineRanking v-reveal :fines="season.fines" :details="season.fineDetails" :category-colors="categoryColors" />
          <PresenceRanking v-reveal :presences="season.presences" :meetings="season.meetings" />
        </div>

        <CashBars v-reveal :subsidies="season.subsidies" />

        <div class="grid grid-cols-[minmax(0,1fr)] gap-7 sm:gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <SquadCarousel v-reveal :members="season.members" />
          <SpotifyCard v-reveal />
        </div>
      </template>
    </main>

    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import CountUp from '@/components/home/CountUp.vue';
import HallOfShame from '@/components/home/HallOfShame.vue';
import CategoryBreakdown from '@/components/home/CategoryBreakdown.vue';
import FineRanking from '@/components/home/FineRanking.vue';
import PresenceRanking from '@/components/home/PresenceRanking.vue';
import CashBars from '@/components/home/CashBars.vue';
import SquadCarousel from '@/components/home/SquadCarousel.vue';
import SpotifyCard from '@/components/home/SpotifyCard.vue';
import SeasonService, { Season } from '@/services/SeasonService';
import { categoryColorMap, formatEuro } from '@/utils/format';

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear];

const year = ref(currentYear);
const season = ref<Season | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const load = async () => {
  loading.value = true;
  error.value = null;
  try {
    season.value = await SeasonService.fetchSeason(year.value);
  } catch (err) {
    console.error('Fehler beim Laden der Saison:', err);
    error.value = 'Die Statistik konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

const categoryColors = computed(() => categoryColorMap(season.value?.categories ?? []));

const kpis = computed(() => {
  const s = season.value;
  if (!s) return [];
  const totalFines = s.fines.reduce((sum, f) => sum + f.value, 0);
  const cash = s.subsidies.reduce((sum, c) => sum + c.value, 0);
  const avgPresence = s.presences.length && s.meetings
    ? s.presences.reduce((sum, p) => sum + p.value, 0) / s.presences.length / s.meetings * 100
    : 0;
  return [
    { label: 'Kasse', value: cash, format: formatEuro, hint: 'Rest-Bargeld gesamt', accent: 'text-beer' },
    { label: 'Strafen gesamt', value: totalFines, format: formatEuro, hint: `${s.fines.length} Sünder`, accent: '' },
    { label: 'Stammtische', value: s.meetings, format: (v: number) => String(Math.round(v)), hint: 'in dieser Saison', accent: '' },
    { label: 'Ø Anwesenheit', value: avgPresence, format: (v: number) => `${Math.round(v)} %`, hint: 'pro Mitglied', accent: 'text-blue-400' },
  ];
});

onMounted(load);
watch(year, load);
</script>
